import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/api/client';

/**
 * Public site settings used to toggle visibility of nav items / routes
 * on the public website. Fed by GET /api/public/settings.
 *
 * Defaults assume everything is visible so the UI stays correct even
 * before the first fetch resolves (prevents a flash-of-hidden-donate).
 */
const DEFAULT_SETTINGS = {
  donate_page_visible: true,
};

const SiteSettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  loading: true,
  refresh: () => {},
});

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/public/settings');
      // Merge over defaults so missing keys from the server don't blow away
      // local defaults (future-proof against adding more toggles).
      setSettings((prev) => ({ ...DEFAULT_SETTINGS, ...prev, ...res.data }));
    } catch (e) {
      // Fail-open: if we can't reach the backend, keep everything visible
      // rather than hiding features the admin didn't ask to hide.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Site settings fetch failed, falling back to defaults:', e?.message || e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Memoize the context value object so consumers (Header nav, Footer, etc.)
  // don't re-render on every parent re-render just because of a new object
  // identity. The underlying data only changes when settings/loading change.
  const value = useMemo(
    () => ({ settings, loading, refresh }),
    [settings, loading, refresh],
  );

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);
